<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          :title="title"
          :showAddNew="true"
          :showExtraBtn="true"
          extraBtnTitle="Calendar View"
          btnTitle="Add New"
          @onClickExtraBtn="navigateToCalendarPage"
          @onClickAddNewButton="onClickToAppointment"
      />
    </div>
    <div class="my-2 px-2">
      <DateQuerySetter
          btnTitle="Go"
          @onClickGo="onClickToGo"
          class="tds-vds-query-setter"
          :isDisabled="shouldDisableDate"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Doctor</label>
          <AsyncSelect
              placeholder="Select Doctor"
              v-model="contactProfileId"
              :api-service="fetchContactProfiles"
              :additional-query="additionalQuery"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Schedule Type</label>
          <v-select
              placeholder="Select Schedule Status"
              :options="activityLogTypeList"
              v-model="activityLogType"
              label="name"
              :reduce="name => name.value"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Schedule Status</label>
          <v-select
              placeholder="Select Schedule Status"
              :options="taskCalendarStatusList"
              v-model="taskCalendarStatus"
              label="name"
              :reduce="name => name.value"
          />
        </div>

      </DateQuerySetter>
    </div>

    <div class="col-12">
      <div class="d-flex justify-content-end px-2">
        <button
            @click="goToPrint"
            :disabled="loading"
            class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
        >
          Print
        </button>
      </div>
    </div>

    <div class="col-12 px-2 mt-2 mb-4">
      <div class="card p-2" style="min-height: 200px">
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
            <tr>
              <th>Patient Name(Patient Id)</th>
              <th>Gender</th>
              <th>Mobile No</th>
              <th>Age</th>
              <th>Schedule Date</th>
              <th>Doctor Name</th>
              <th>Service Name</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in tableItems"
                :key="i"
                :style="{
                    backgroundColor: taskStatusColors[item.status].bg || '#f9fafb',
                    color: taskStatusColors[item.status].text || '#111827',
                  }"
            >
              <td>{{ item.patient_name }}({{ item.patient_serial_no }})</td>
              <td>{{ convertToTitleCase(item.patient_gender) }}</td>
              <td>{{ item.patient_mobile_no }}</td>
              <td>{{ item.patient_age }}</td>
              <td>{{ item.start_time }}</td>
              <td>{{ item.doctor_name }}</td>
              <td>{{ item.product_name }}</td>
              <td>{{ convertToTitleCase(item.status) }}</td>
              <td>
                <button v-if="isDoctor" @click="goToPrescription(item, 'redirect')" class="btn btn-sm"><i class="fas fa-pencil"></i></button>
                <button @click="goToPrescription(item, 'print')" v-if="item.service_type === 'treatment_plan' && item.prescription_count > 0" class="btn btn-primary btn-sm"><i class="fas fa-print"></i></button>
              </td>
            </tr>
            </tbody>
          </table>
          <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
        </div>
      </div>
    </div>

    <div class="px-2 position-absolute bottom-0 mt-4">
      <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <AddAppointmentModal
        v-if="store.state.isModalOpen"
        @onSubmit="getDentalScheduleList"
    />

    <Loader v-if="loading"/>

  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import {inject, onMounted, reactive, ref} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import { computed } from 'vue';
import handleDentalOs from '@/services/modules/dental-os/dentalOs';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from '@/services/modules/contact'
import AddAppointmentModal from '@/components/molecule/company/dental-os/appointment/AddAppointmentModal.vue';
import handleCompany from "@/services/modules/company";
import dentalSchedulePdf from '@/services/utils/pdf/dentalSchedulePdf';
import handleChartAccount from '@/services/modules/chartAccount'
import { taskStatusColors, convertToTitleCase } from '@/services/utils/global.js';
import writeDentalPrescriptionHelper from '@/services/utils/pdf/writeDentalPrescriptionHelper';


const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const contactProfile = ref('');
const loading = ref(false)
const tableItems = ref([]);
const dentalScheduleData = ref([]);
const params = ref({
  offset: 20,
});
const startDate = ref('');
const endDate = ref('');
const contactProfileId = ref('');
const additionalQuery = {
  roles: "doctor,medical_officer",
}
const taskCalendarStatusList = ref([]);
const taskCalendarStatus = ref('');
const activityLogTypeList = ref([]);
const activityLogType = ref('');


const data = ref(null);
const leftSideNotes = ref([]);
const rightSideNotes = ref([]);
const service = ref(null);
const latestOrder = ref(null);

const token = computed(() => localStorage.getItem('token'));
const routeFrom = computed(() => route.query.from || null);
const shouldDisableDate = computed(() => ['today_treatment_count', 'tomorrow_treatment_count'].includes(routeFrom.value));

const title = computed(() => {
  let title = 'Dental Schedule';

  if (routeFrom.value === 'today_treatment_count') {
    title += ' (Today)';
  } else if (routeFrom.value === 'tomorrow_treatment_count') {
    title += ' (Tomorrow)';
  }

  return title;
});

let companyInfo = reactive({})
const { fetchDentalScheduleList, fetchSingleDentalSchedule, fetchSingleDoctorOrder } = handleDentalOs();
const { fetchContactProfiles } = handleContact()
const { fetchCompanyInfo } = handleCompany();
const { exportToPDF } = dentalSchedulePdf();
const { fetchHome } = handleChartAccount()
const { generatePdf } = writeDentalPrescriptionHelper();

const isDoctor = computed(() => store.getters['user/isDoctor']);

const onClickToGo = () => {
  getDentalScheduleList();
}

const onClickToAppointment = () => {
  store.state.isModalOpen = true
}

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

function navigateToCalendarPage() {
  router.push({name: `dental-schedule`, params: route.params, query: route.query});
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getDentalScheduleList();
}

const getDentalScheduleList = async () => {
  const query = getQuery();
  loading.value = true
  tableItems.value = []

  await fetchDentalScheduleList(query).then(res => {
    if (!res.status){
      store.commit("resetPagination")
      return
    }
    tableItems.value = res.data.data;
    setPagination(res.data)
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const goToPrescription = async (item, type) => {

    loading.value = true;
    try {
        const res = await fetchSingleDentalSchedule(getQuery(), item.id);
        if (!res.status) return showError(res.message);
        
        let response = res.data;

        response.task_id = item.task_id || null;

        if (type === 'print'){
            return onClickToPrint(response)
        }

        if (response.type == 'treatment_plan'){
            onClickToCreatePrescription(response)
        }

        if (response.type == 'dental_appointment'){
            onClickToClose(response)
        }

    } finally {
        loading.value = false;
    }
}

const onClickToPrint = async (item) => {
    const query = getPrintQuery(item);
    const serviceId = item.service_general_id;

    const isHideFooter = true;
    const isHideHeader = true;

    loading.value = true;

    await fetchSingleDoctorOrder(query, serviceId).then((res) => {
        if (!res.status) return showError(res.message)
        data.value = res.data;
        setDoctorOrderData();
    }).then(() => {
        generatePdf(
            companyInfo.value, 
            leftSideNotes.value, 
            rightSideNotes.value, 
            service.value, 
            isHideHeader, 
            isHideFooter
        )
    })
    .finally(() => {
        loading.value = false
    });
}

const setDoctorOrderData = () => {

    leftSideNotes.value = data.value.prescription_details.left_side_notes;
    rightSideNotes.value = data.value.prescription_details.right_side_notes;
    latestOrder.value = data.value.latest_prescription ? data.value.latest_prescription.doctor_order : null;
    service.value = data.value.bill;

    const doctorOrder = service.value.doctor_order;

    if(! doctorOrder) return;

    if(doctorOrder.investigation_orders.length){
        setInvestigationOrderData(doctorOrder.investigation_orders, 'investigation');
    }

    if(doctorOrder.medicine_orders.length){
        setMedicineOrderData(doctorOrder.medicine_orders);
    }

    if(doctorOrder.notes.length){
        setNoteData(doctorOrder.notes);
    }
}

const setMedicineOrderData = (medicineRx) => {
    
    let rxNote = rightSideNotes.value.find(item => item.value === 'rx');
    if(!rxNote) return;
    
    let formattedMedicineRx = formatMedicineRx(medicineRx);
    rxNote.selectedNotes = formattedMedicineRx;
}

const formatMedicineRx = (medicineRx) => {
    return medicineRx.map((medicine) => {
        let item = {
            id: medicine.id,
            product_id: medicine.product_id,
            service_resource_id: medicine.service_resource,
            name: medicine.product.name,
            remarks: medicine.remarks,
            is_selected: true,
            is_edited: true,
            dose: medicine.dose,
            unit: medicine.unit,
            duration: medicine.duration,
            madication_status: medicine.madication_status,
            is_published: medicine.is_published,
            group_index: medicine.group_index,
            order_date: medicine.order_date,
            created_at: medicine.created_at,
            status: medicine.status
        };

        return item;
    })
}

const setInvestigationOrderData = (medicineRx, noteType) => {

    let prevNote = leftSideNotes.value.find(item => item.value === noteType);
    if(!prevNote) return;

    for(let note of medicineRx){

        const noteObj = {
            id: note.id,
            product_id: note.product_id,
            name: note.product.name,
            remarks: note.remarks,
            is_selected: true,
            status: 'active',
            is_published: note.is_published,
        };

        prevNote.selectedNotes.push(noteObj)
    }
}

const setNoteData = (notes) => {
    const leftSideNoteKeys = leftSideNotes.value.reduce((acc, item, index) => {
                    acc[item.value] = index;
                    return acc;
                }, {});

    const rightSideNoteKeys = rightSideNotes.value.reduce((acc, item, index) => {
                acc[item.value] = index;
                return acc;
            }, {});

    notes.forEach((noteItem) => {
        
        const noteIndex = rightSideNoteKeys[noteItem.type];

        if(noteIndex !== undefined){
            rightSideNotes.value[noteIndex].id = noteItem.id;
            rightSideNotes.value[noteIndex].selectedNotes = noteItem.note;
            rightSideNotes.value[noteIndex].markedPositions = noteItem.marked_positions;
            return;
        }

        const leftSideNoteIndex = leftSideNoteKeys[noteItem.type];
        
        if(leftSideNoteIndex !== undefined){
            leftSideNotes.value[leftSideNoteIndex].id = noteItem.id;
            leftSideNotes.value[leftSideNoteIndex].selectedNotes = noteItem.note;
            leftSideNotes.value[leftSideNoteIndex].markedPositions = noteItem.marked_positions;
        }
    });
}

const getPrintQuery = (item) => {
    let query = '?company_id=' + route.params.companyId;
    query += `&sales_order_general_id=${item.activitylogable_id}`
    return query;
}

const goToPrint = async () => {
  let query = getQuery();
  query += '&is_all=1';

  loading.value = true;
  dentalScheduleData.value = [];

  try {
    const res = await fetchDentalScheduleList(query);

    if (!res.status) {
      showError(res.message);
      return;
    }

    dentalScheduleData.value = res.data;
    exportToPDF(companyInfo, dentalScheduleData.value);
  } catch (err) {
    console.log("Something went wrong");
  } finally {
    loading.value = false;
  }
}

const onClickToCreatePrescription = (item) => {

  const redirect = {
    name: 'dental-service',
    params: {
        ...route.params,
        serviceId: item.service_general_id
    },
    query: {
        ...route.query,
        general_id: item.sale_order_general_id,
        ipd_register_id: item.ipd_register_id,
        contact_id: item.ipd_contact_profile_id,
        service_status: item.status === 'completed' ? 'completed_service' : 'ongoing_service',
        task_id: item.task_id,
        from: 'list',
        back_url: encodeURIComponent(route.fullPath)
    }
  };

  router.push(redirect)
}

const onClickToClose = (response) => {
    const redirectObj = {
        name: 'dental-service',
        params: {
            ...route.params,
            serviceId: response.activitylogable_id
        },
        query: {
            ...route.query,
            service_status: "completed_service",
            contact_id: response.ipd_contact_profile_id,
            ipd_register_id: response.ipd_register_id,
            general_id: response.sales_order_general_id,
            from: 'list',
            back_url: encodeURIComponent(route.fullPath)
        }
    };

    if (! response.sales_order_general_id){
        redirectObj.query = {
            ...route.query,
            back_url: encodeURIComponent(route.fullPath)
        };
    }
    
    router.push(redirectObj)
}

const setCompanyInfo = async () => {
  await fetchCompanyInfo(route.params.companyId).then((res) => {
    companyInfo = res.data;
  }).catch((error) => {
    console.log(error);
  });
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}`;
  if (route.query.page) query += '&page=' + route.query.page;
  if (route.query.start) query += '&start_date=' + route.query.start;
  if (route.query.end) query += '&end_date=' + route.query.end;
  if (contactProfileId.value) query += `&contact_profile_id=${contactProfileId.value.id}`;
  if (taskCalendarStatus.value) query += `&status=${taskCalendarStatus.value}`;
  if (activityLogType.value) query += `&type=${activityLogType.value}`;
  query += '&offset=' + params.value.offset
  return query;
}

onMounted(() => {
  Promise.all([
    getDentalScheduleList(),
    setCompanyInfo(),
    fetchHome().then(res => {
      if (!res.data) {
        return;
      }
      if (res.data && res.data.task_calendar_status){
        taskCalendarStatusList.value = res.data.task_calendar_status;
      }
      if (res.data && res.data.activityLogs_types){
        activityLogTypeList.value = res.data.activityLogs_types.filter(item =>
            ['dental_appointment', 'treatment_plan'].includes(item.value)
        )
      }
    })
  ])
})

</script>
